// Несоответствия заказа в его карточке (модуль quality-records): список,
// карантин, регистрация нового.  Без модуля блока нет.  Регистрирует
// quality.manage или manufacturing_order.advance; решение и закрытие — в реестре.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { hasModule } from "@/lib/auth";
import { useAuth } from "@/lib/AuthContext";
import { ApiError, manufacturingOrdersApi, qualityApi, stagesApi, type Nonconformity } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const FIELD_CLS = "px-3 py-2 rounded-md bg-surface border border-border";
const INPUT_CLS = `w-full ${FIELD_CLS}`;

/** Текст ошибки API: известный код — переведённым текстом. */
export function useQualityError() {
  const { t } = useTranslation();
  return (e: unknown) =>
    e instanceof ApiError
      ? t(`quality.errors.${e.code}`, { defaultValue: e.message })
      : e instanceof Error
        ? e.message
        : String(e);
}

export function NcStatus({ n }: { n: Nonconformity }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex gap-1">
      <span className={`pill text-xs ${n.status === "OPEN" ? "pill-warn" : "pill-ok"}`}>{t(`quality.status.${n.status}`)}</span>
      {n.quarantine && <span className="pill pill-danger text-xs">{t("quality.nc.quarantine")}</span>}
    </span>
  );
}

export function OrderNonconformities({ orderId }: { orderId: string }) {
  const { user, hasPermission } = useAuth();
  if (!hasModule(user, "quality-records") || !hasPermission("manufacturing_order.read")) return null;
  return <Block orderId={orderId} canRegister={hasPermission("quality.manage") || hasPermission("manufacturing_order.advance")} />;
}

function Block({ orderId, canRegister }: { orderId: string; canRegister: boolean }) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const list = useQuery({
    queryKey: ["quality", "nonconformities", { orderId }],
    queryFn: () => qualityApi.nonconformities({ manufacturing_order_id: orderId, page_size: 100 }),
  });
  const items = list.data?.items ?? [];
  const quarantined = items.filter((n) => n.quarantine).length;

  return (
    <section className="card p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg">
          {t("quality.nc.blockTitle")}
          {quarantined > 0 && (
            <span className="pill pill-danger text-xs ml-2">{t("quality.nc.quarantineCount", { count: quarantined })}</span>
          )}
        </h2>
        <div className="flex gap-2">
          {canRegister && !adding && (
            <button className="btn btn-sm" onClick={() => setAdding(true)}>
              {t("quality.nc.register")}
            </button>
          )}
          <Link to={`/admin/nonconformities?order=${orderId}`} className="btn btn-sm">
            {t("quality.nc.allForOrder")}
          </Link>
        </div>
      </div>

      {adding && <RegisterForm orderId={orderId} onDone={() => setAdding(false)} />}

      {items.length === 0 ? (
        <p className="text-sm text-text-muted">{list.isLoading ? t("common.loading") : t("quality.nc.none")}</p>
      ) : (
        <ul className="text-sm divide-y divide-border">
          {items.map((n) => (
            <li key={n.id} className="py-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="font-mono">{n.number}</span>
                <span className="text-text-muted"> · {formatDateTime(n.opened_at)}</span>
                <p className="truncate">{n.description}</p>
              </div>
              <NcStatus n={n} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RegisterForm({ orderId, onDone }: { orderId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const errText = useQualityError();
  const [stage, setStage] = useState("");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [quarantine, setQuarantine] = useState(true);

  // Те же ключи, что у чеклиста стадий и названий стадий карточки: без лишних запросов.
  const route = useQuery({
    queryKey: ["mo-short-flow", orderId],
    queryFn: () => manufacturingOrdersApi.stages(orderId),
  });
  const catalog = useQuery({
    queryKey: ["stages", "names"],
    queryFn: () => stagesApi.list({ page_size: 200 }),
    staleTime: 5 * 60_000,
  });
  const stageOf = (code: string) => catalog.data?.items.find((s) => s.code === code);
  const reasons = stage ? (stageOf(stage)?.config?.exception_signals ?? []) : [];

  const create = useMutation({
    mutationFn: () =>
      qualityApi.createNonconformity({
        manufacturing_order_id: orderId,
        ...(stage ? { stage_code: stage } : {}),
        ...(reason ? { reason_code: reason } : {}),
        description: description.trim(),
        ...(quantity ? { quantity: Number(quantity) } : {}),
        ...(unit.trim() ? { unit: unit.trim() } : {}),
        quarantine,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quality", "nonconformities"] });
      onDone();
    },
  });

  return (
    <div className="border border-border rounded-md p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <select
          className={INPUT_CLS}
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            setReason("");
          }}
        >
          <option value="">{t("quality.nc.stage")} —</option>
          {(route.data?.stages ?? []).map((s) => (
            <option key={s.stage_code} value={s.stage_code}>
              {stageOf(s.stage_code)?.name ?? s.stage_code}
            </option>
          ))}
        </select>
        <select className={INPUT_CLS} value={reason} onChange={(e) => setReason(e.target.value)} disabled={reasons.length === 0}>
          <option value="">{t("quality.nc.reason")} —</option>
          {reasons.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label_ru}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className={INPUT_CLS}
        rows={3}
        placeholder={t("quality.nc.description")}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-3">
        <input
          className={`${FIELD_CLS} w-32`}
          type="number"
          min={0}
          step="any"
          placeholder={t("quality.nc.quantity")}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
        />
        <input
          className={`${FIELD_CLS} w-24`}
          placeholder={t("quality.nc.unit")}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={quarantine} onChange={(e) => setQuarantine(e.target.checked)} />
          {t("quality.nc.quarantine")}
        </label>
      </div>
      {create.isError && <ErrorBanner onDismiss={() => create.reset()}>{errText(create.error)}</ErrorBanner>}
      <div className="flex gap-2">
        <button
          className="btn btn-sm btn-primary"
          disabled={!description.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {t("quality.nc.register")}
        </button>
        <button className="btn btn-sm" onClick={onDone}>
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}
